import * as React from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  createRoutesFromElements,
  Route,
  Navigate,
} from 'react-router-dom';
import {Login} from '../pages/Auth/Login';
import {Home} from '../pages/Home';
import {
  confirmEmailLoader,
  createSubscriptionLoader,
  exploreRouteLoader,
  featureRouteLoader,
  homeLoader,
  landingLoader,
  permissionRouteLoader,
  rootLoader,
  secureRouteLoader,
  workspaceRouteLoader,
} from './loaders';
import {Library} from '../pages/Library/Root';
import {DashboardRoot} from './rootElements/DashboardRoot';
import AuthRoot from './rootElements/AuthRoot';
import {CoursePreview, CoursesList, CreateCourse} from '../pages/Courses';
import {CreateStudent} from '../pages/Learners/Create';
import {ListLearners, CreateLearners} from '../pages/Learners';
import {Signup} from '../pages/Auth/Signup';
import {ConfirmEmail} from '../pages/Auth/ConfirmEmail';
import {ResetPassword, SendForgotPassword} from '../pages/Auth/ForgotPassword';
import PermissionError from '../pages/PermissionError';
import {APP_PERMISSIONS} from '../data/permissions';
import {appText} from '../assets/text/appText';
import DashboardSettingsRoot from './rootElements/DashboardSettingsRoot';
import {GeneralSettings} from '../pages/Settings/GeneralSettings';
import {RoleList} from '../pages/Settings/RoleSettings';
import {
  StaffEdit,
  StaffInvite,
  StaffList,
} from '../pages/Settings/StaffSettings';
import {Folders} from '../pages/Folders';
import EditorRoot from './rootElements/EditorRoot';
import {UserProfileRoot} from './rootElements/UserProfileRoot';
import {SettingsUserProfile} from '../pages/UserProfile/SettingsUserProfile';
import {NotificationsUserProfile} from '../pages/UserProfile/NotificationsUserProfile';
import {Lessons} from '../pages/Lessons';
import {CourseHome} from '../pages/CourseHome';
import {CourseEditRoot} from './rootElements/CourseEditRoot';
import {GeneralCourseEdit} from '../pages/CourseDetails/CourseEdit';
import {CourseLearner} from '../pages/CourseDetails/CourseLearners';
import {CourseInstructor} from '../pages/CourseDetails/CourseInstructors';
import {AdminPanel} from '../pages/AdminPanel';
import NotFound from '../modules/NotFound';
import {LTIPlayer} from '../pages/Integrations/LTIPlayer';
import {LtiSettings} from '../pages/Settings/LtiSettings';
import {SubscriptionSettings} from '../pages/Settings/SubscriptionSettings';
import DesktopLearning from '../pages/DesktopLearning';
import {AiSettings} from '../pages/Settings/AiSettings';
import {AppSettings} from '../pages/Settings/AppSettings';
import {StorageSettings} from '../pages/Settings/StorageSettings';
import SubscriptionSuccess from '../pages/Subscription/SubscriptionSuccess';
import {StorageIntegration} from '../pages/Settings/StorageIntergrationSettings';
import {Explore} from '../pages/Explore';
import {isLimitedSandbox, isSandbox} from '../utils/envHelper';
import {ScormPlayer} from '../pages/Integrations/SCORMPlayer';
import GoogleOAuth from '../pages/Auth/GoogleOAuth';
import {SecurityUserProfile} from '../pages/UserProfile/SecurityUserProfile';
import {inject, observer} from 'mobx-react';
import NavigationStore from './store/NavigationStore';
import {
  ORGANIZATION_SUBSCRIPTION_ACTIVE,
  OrganizationDetails,
} from '../data/models/Oganization/Organization';
import {SelectSubscription} from '../pages/Subscription/SelectSubscription';
import {SubscriptionFirstPayment} from '../pages/Subscription/SubscriptionFirstPayment';
import {
  LTI_SUB_FEATURE,
  STORAGE_INTEGRATION_SUB_FEATURE,
} from '../data/models/Subscription/SubscriptionPermission';
import {Marketplace} from '../pages/Subscription/Marketplace';
import TapyblAIHome from '../pages/TapyblAI/Home';
import {GenerateAIVideo} from '../pages/TapyblAI/GenerateAIVideo';
import {AiAssistant, AiAssistantChatPage} from '../pages/TapyblAI/AIAssistant';
import {
  EditAIAssistant,
  GenerateAIAssistant,
} from '../pages/TapyblAI/GenerateAIAssistant';
import {Analytics} from '../pages/Analytics/GeneralCoursesAnalytics';
import {CourseAnalyticsLearners} from '../pages/Analytics/CourseLevelAnalytics/CourseAnalyticsLearners';
import {LessonAnalyticsQuestions} from '../pages/Analytics/LessonAnalyticsQuestions';
import {LessonAnalyticsBranches} from '../pages/Analytics/LessonAnalyticsBranches';
import {CourseLevelAnalyticsRoot} from './rootElements/CourseLevelAnalyticsRoot';
import {CourseAnalyticsOverview} from '../pages/Analytics/CourseLevelAnalytics/CourseAnalyticsOverview';
import {CourseAnalyticsComments} from '../pages/Analytics/CourseLevelAnalytics/CourseAnalyticsComments';
import {CourseAnalyticsLikes} from '../pages/Analytics/CourseLevelAnalytics/CourseAnalyticsLikes';
import {GenerateMicrolesson} from '../pages/TapyblAI/GenerateMicrolesson';
import {GenerateMicroreel} from '../pages/TapyblAI/GenerateMicroreel';
import {MicrolessonsList} from '../pages/Microlessons/List';
import {MicroreelsList} from '../pages/Microreels/List';
import {CreatePost, PostDetails, PostsList} from '../pages/Posts';
import {ProjectDetails, ProjectsList} from '../pages/Projects';
import GeneratingMicroreel from '../pages/Microreels/Generating';
import {CreateFeedback, FeedbackDetails, FeedbackList} from '../pages/Feedback';
import {CourseLessonEditor} from '../pages/CourseLessonEditor';
import {MicrolessonEditor} from '../pages/Microlessons/Editor';
import {CourseVideoPlayback} from '../pages/CourseVideoPlayback';
import {MicrolessonsPreview} from '../pages/Microlessons/Preview';
import {VideoEditor} from '../pages/VideoEditor';
import AuthRoot2 from './rootElements/AuthRoot2/AuthRoot2';
import SubscriptionCancel from '../pages/Subscription/SubscriptionCancel/SubscriptionCancel';
import UserInfoStore from '../stores/UserInfoStore';
import {reloadOnChunkError} from '../utils/reloadOnChunkError';
import {UserInfo} from '../data/models/User/UserInfo';
// After a deploy an open tab may ask for chunks the new build no longer has; it reloads once instead.
const loadStudio = () =>
  reloadOnChunkError(() => import('../modules/MicroreelStudio'));
const StudioWorkspace = React.lazy(async () => ({
  default: (await loadStudio()).StudioWorkspace,
}));
const StudioCreate = React.lazy(async () => ({
  default: (await loadStudio()).StudioCreate,
}));
const StudioLibrary = React.lazy(async () => ({
  default: (await loadStudio()).StudioLibrary,
}));
const StudioStoryboard = React.lazy(async () => ({
  default: (await loadStudio()).StudioStoryboard,
}));
const StudioReview = React.lazy(async () => ({
  default: (await loadStudio()).StudioReview,
}));
const StudioPublish = React.lazy(async () => ({
  default: (await loadStudio()).StudioPublish,
}));

function StudioRoute({children}: {children: React.ReactNode}) {
  return (
    <React.Suspense fallback={<div role="status">{appText.loadingStudio}</div>}>
      {children}
    </React.Suspense>
  );
}

const router = (
  permissions: string[],
  subscriptionState: number,
  userOrganization?: OrganizationDetails,
  features?: any,
  user?: UserInfo,
) => {
  const analyticsAvailable =
    subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE;

  return createBrowserRouter(
    createRoutesFromElements([
      <Route
        path="/scorm/:lessonId/:lessonSecret"
        element={<ScormPlayer />}
        key="scorm"
      />,
      <Route path="/" key="/" loader={rootLoader} />,
      <Route
        path="/subscription_tier"
        element={<SelectSubscription />}
        key="/subscription_tier"
      />,
      <Route
        path="/permission_error"
        element={<PermissionError />}
        key="/permission_error"
      />,
      <Route
        path="/admin_panel"
        element={<AdminPanel />}
        key="admin_panel"
        loader={secureRouteLoader}
      />,
      <Route path="/auth" element={<AuthRoot />} key="/auth">
        <Route path="google-oauth" element={<GoogleOAuth />} />
        <Route path="login" loader={landingLoader} element={<Login />} />
        <Route
          path="forgot_password"
          loader={landingLoader}
          element={<SendForgotPassword />}
        />
        <Route
          path="reset_password"
          loader={landingLoader}
          element={<ResetPassword />}
        />
      </Route>,
      <Route path="/auth" element={<AuthRoot2 />} key="/auth2">
        {!isSandbox && (
          <Route path="signup" loader={landingLoader} element={<Signup />} />
        )}
        {!isSandbox && (
          <Route
            path="confirm_email"
            loader={confirmEmailLoader}
            element={<ConfirmEmail />}
          />
        )}
      </Route>,
      <Route
        path="/home"
        loader={homeLoader(permissions, userOrganization)}
        element={<Home />}
        key="/home"
      />,
      <Route
        path="/explore"
        loader={exploreRouteLoader(userOrganization)}
        element={<Explore />}
        key={'/explore'}
      />,
      <Route
        path="/course/:courseId"
        element={<CourseHome />}
        key="/course"
        loader={secureRouteLoader}
      />,
      <Route
        path="/course/:courseId/lesson/:lessonId"
        element={<CourseVideoPlayback />}
        loader={secureRouteLoader}
        key="/course"
      />,
      <Route path="/subscription" key="subscription">
        <Route
          path=":organizationId/success"
          element={<SubscriptionSuccess />}
          key="/sub_success"
        />
        <Route
          path=":organizationId/cancel"
          element={<SubscriptionCancel />}
          key="/sub_cancel"
        />
        <Route
          path=":organizationId/success_update"
          element={<SubscriptionSuccess forUpdate />}
          key="/sub_success_update"
        />
        <Route
          path=":organizationId/payment_success"
          element={<SubscriptionSuccess forPayment />}
          key="/payment_success"
        />
        <Route
          path=":organizationId/trial_success"
          element={<SubscriptionSuccess forTrial />}
          key="/trial_success"
        />
        <Route
          path=":organizationId/first_payment"
          element={<SubscriptionFirstPayment />}
          key="/first_payment"
          loader={createSubscriptionLoader(
            permissions,
            subscriptionState,
            userOrganization?.id,
          )}
        />
      </Route>,
      <Route
        path=":organizationId/marketplace"
        key="/marketplace"
        element={<Marketplace />}
        loader={permissionRouteLoader(
          APP_PERMISSIONS.ORG_MANAGE_SETTINGS,
          permissions,
          subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
          userOrganization?.id,
        )}
      />,
      <Route
        path="/manage/:organizationId"
        loader={workspaceRouteLoader(
          subscriptionState,
          userOrganization?.selectedSubscriptionType,
          userOrganization,
        )}
        element={<DashboardRoot />}
        key="/manage">
        <Route path="workspace" element={<Navigate to="../home" replace />} />
        <Route
          path="desktop_learning"
          element={<DesktopLearning />}
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_ACCESS_LIBRARY,
            permissions,
            true,
            userOrganization?.id,
          )}
        />
        <Route
          path="library"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_ACCESS_LIBRARY,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
          element={<Library />}
        />
        <Route
          path="home"
          element={<TapyblAIHome username={user?.name} />}
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_ACCESS_LIBRARY,
            permissions,
            true,
            userOrganization?.id,
          )}
        />
        <Route
          path="home/generate_video"
          element={<GenerateAIVideo />}
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_ACCESS_LIBRARY,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
        />
        <Route
          path="home/generate_microlesson"
          element={<GenerateMicrolesson />}
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_ACCESS_LIBRARY,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
        />
        <Route
          path="home/generate_microreel"
          element={<GenerateMicroreel />}
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_EDIT_COURSES,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
        />
        <Route
          path="home/split_video_into_microreels"
          element={<GenerateMicroreel />}
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_EDIT_COURSES,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
        />
        <Route
          path="microreels/studio/new"
          element={
            <StudioRoute>
              <StudioCreate />
            </StudioRoute>
          }
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_EDIT_COURSES,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
        />
        <Route
          path="microreels/studio"
          element={
            <StudioRoute>
              <StudioWorkspace />
            </StudioRoute>
          }
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_EDIT_COURSES,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}>
          <Route
            index
            element={
              <StudioRoute>
                <StudioLibrary />
              </StudioRoute>
            }
          />
          <Route
            path="microreels/:id/storyboard"
            element={
              <StudioRoute>
                <StudioStoryboard />
              </StudioRoute>
            }
          />
          <Route
            path="microreels/:id/review"
            element={
              <StudioRoute>
                <StudioReview />
              </StudioRoute>
            }
          />
          <Route
            path="microreels/:id/publish"
            element={
              <StudioRoute>
                <StudioPublish />
              </StudioRoute>
            }
          />
        </Route>
        <Route
          path="ai_assistant"
          element={<AiAssistant />}
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_ACCESS_LIBRARY,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
        />
        <Route
          path="ai_assistant/generate_ai_assistant"
          element={<GenerateAIAssistant />}
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_ACCESS_LIBRARY,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
        />
        <Route
          path="ai_assistant/edit_ai_assistant/:assistantId"
          element={<EditAIAssistant />}
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_ACCESS_LIBRARY,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
        />
        <Route path="videoEditor/:mediaFileId" element={<VideoEditor />} />
        <Route
          path="courses/:courseId"
          element={<CourseEditRoot />}
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_EDIT_COURSES,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
          key="/courses">
          <Route path="general" element={<GeneralCourseEdit />} />
          <Route path="learners" element={<CourseLearner />} />
          <Route path="instructors" element={<CourseInstructor />} />
        </Route>
        <Route
          path="courses"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_LIST_COURSES,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
          element={<CoursesList />}
        />
        <Route
          path="courses/create"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_CREATE_COURSES,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
          element={<CreateCourse />}
        />
        {!isLimitedSandbox && (
          <Route
            path="learners/create"
            loader={permissionRouteLoader(
              APP_PERMISSIONS.ORG_LIST_LEARNERS,
              permissions,
              subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
              userOrganization?.id,
            )}
            element={<CreateLearners />}
          />
        )}
        <Route
          path="analytics"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_LIST_LEARNERS,
            permissions,
            analyticsAvailable,
            userOrganization?.id,
          )}
          element={<Analytics />}
        />
        <Route
          path="analytics/course/:courseId"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_LIST_LEARNERS,
            permissions,
            analyticsAvailable,
            userOrganization?.id,
          )}
          element={<CourseLevelAnalyticsRoot />}>
          <Route index element={<Navigate to="learners" replace />} />
          <Route path="learners" element={<CourseAnalyticsLearners />} />
          <Route path="overview" element={<CourseAnalyticsOverview />} />
          <Route path="comments" element={<CourseAnalyticsComments />} />
          <Route path="likes" element={<CourseAnalyticsLikes />} />
        </Route>

        <Route
          path="analytics/:courseId/lessons/question_details"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_LIST_LEARNERS,
            permissions,
            analyticsAvailable,
            userOrganization?.id,
          )}
          element={<LessonAnalyticsQuestions />}
        />
        <Route
          path="analytics/:courseId/lessons/branches_details"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_LIST_LEARNERS,
            permissions,
            analyticsAvailable,
            userOrganization?.id,
          )}
          element={<LessonAnalyticsBranches />}
        />
        <Route
          path="microreels"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_LIST_COURSES,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
          element={<MicroreelsList />}
        />
        <Route
          path="microreels/:groupId"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_LIST_COURSES,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
          element={<MicroreelsList />}
        />
        <Route
          path="projects"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_LIST_COURSES,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
          element={<ProjectsList />}
        />
        <Route
          path="projects/:projectId"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_LIST_COURSES,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
          element={<ProjectDetails />}
        />
        <Route
          path="posts"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_LIST_COURSES,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
          element={<PostsList />}
        />
        <Route
          path="posts/:postId"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_LIST_COURSES,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
          element={<PostDetails />}
        />
        <Route
          path="home/create_post"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_CREATE_COURSES,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
          element={<CreatePost />}
        />
        <Route
          path="microreels/generating"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_LIST_COURSES,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
          element={<GeneratingMicroreel />}
        />
        <Route
          path="feedback_questions"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_LIST_COURSES,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
          element={<FeedbackList />}
        />
        <Route
          path="feedback_questions/create"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_CREATE_COURSES,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
          element={<CreateFeedback />}
        />
        <Route
          path="feedback_questions/:feedbackId"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_LIST_COURSES,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
          element={<FeedbackDetails />}
        />
        <Route
          path="microlessons"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_LIST_COURSES,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
          element={<MicrolessonsList />}
        />
        <Route
          path="learners"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_LIST_LEARNERS,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
          element={<ListLearners />}
        />
        <Route
          path="students/create"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_CREATE_LEARNERS,
            permissions,
            subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            userOrganization?.id,
          )}
          element={<CreateStudent />}
        />
        <Route
          path="settings"
          loader={permissionRouteLoader(
            APP_PERMISSIONS.ORG_MANAGE_SETTINGS,
            permissions,
            true,
            userOrganization?.id,
          )}
          element={<DashboardSettingsRoot />}>
          <Route path="general" element={<GeneralSettings />} />
          <Route path="ai" element={<AiSettings />} />
          <Route path="app_settings" element={<AppSettings />} />
          <Route path="storage" element={<StorageSettings />} />
          <Route path="roles" element={<RoleList />} />
          <Route path="users" element={<StaffList />} />
          {!isLimitedSandbox && (
            <Route path="invite_users" element={<StaffInvite />} />
          )}
          <Route path="edit_user/:memberId" element={<StaffEdit />} />
          <Route
            path="lti_settings"
            element={<LtiSettings />}
            loader={featureRouteLoader(
              LTI_SUB_FEATURE,
              features || {},
              subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            )}
          />
          <Route
            path="storage_integration"
            element={<StorageIntegration />}
            loader={featureRouteLoader(
              STORAGE_INTEGRATION_SUB_FEATURE,
              features || {},
              subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
            )}
          />
          <Route path="subscription" element={<SubscriptionSettings />} />
        </Route>
      </Route>,
      <Route
        path="preview/:organizationId"
        loader={permissionRouteLoader(
          APP_PERMISSIONS.ORG_EDIT_COURSES,
          permissions,
          subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
          userOrganization?.id,
        )}
        key={'preview'}
        errorElement={<div>ERROR</div>}>
        <Route
          path="course/:courseId"
          key="course_preview"
          element={<CoursePreview />}
          errorElement={<div>ERROR</div>}
        />
        <Route
          path="course/:courseId/lesson/:lessonId"
          element={<CourseVideoPlayback preview />}
          key={Math.random().toString()}
        />
        <Route
          path="microlesson/:lessonId"
          element={<MicrolessonsPreview />}
          key={Math.random().toString()}
        />
      </Route>,
      <Route
        path="/editor/:organizationId"
        key="editor"
        element={<EditorRoot />}
        loader={permissionRouteLoader(
          APP_PERMISSIONS.ORG_EDIT_COURSES,
          permissions,
          subscriptionState === ORGANIZATION_SUBSCRIPTION_ACTIVE,
          userOrganization?.id,
        )}>
        <Route path="courses/:courseId/folders" element={<Folders />} />
        <Route
          path="courses/:courseId/folders/:folderId/lessons"
          element={<Lessons />}
        />
        <Route
          path="courses/:courseId/folders/:folderId/lessons/:lessonId/edit"
          element={<CourseLessonEditor />}
        />
        <Route path="microlessons/:lessonId" element={<MicrolessonEditor />} />
      </Route>,
      <Route
        path="/player/:lessonId"
        loader={secureRouteLoader}
        element={<CourseVideoPlayback />}
        key="/playback"
      />,
      <Route
        path="/profile"
        element={<UserProfileRoot />}
        loader={secureRouteLoader}
        key="/profile">
        <Route
          path="profile_settings"
          loader={secureRouteLoader}
          element={<SettingsUserProfile />}
        />
        <Route
          path="profile_security"
          loader={secureRouteLoader}
          element={<SecurityUserProfile />}
        />
        <Route
          path="notifications"
          loader={secureRouteLoader}
          element={<NotificationsUserProfile />}
        />
      </Route>,
      <Route
        path="/lti_player/:lessonSecret"
        element={<LTIPlayer />}
        key="lti_player"
      />,
      <Route
        key={'ai_assistant_chat'}
        path=":organizationId/ai_assistant/:assistantId"
        element={<AiAssistantChatPage />}
      />,
      <Route path="*" element={<NotFound />} key="not_found" />,
    ]),
  );
};

interface Props {
  navigationStore?: NavigationStore;
  userInfoStore?: UserInfoStore;
}

const AppRouter = inject(
  'navigationStore',
  'userInfoStore',
)(
  observer(({navigationStore, userInfoStore}: Props) => {
    const [loadingData, setLoadingData] = React.useState(true);

    React.useEffect(() => {
      initPermissionData();
    }, [navigationStore?.userOrganization]);

    const initPermissionData = async () => {
      const user = navigationStore!.getStoredUser();
      if (!user) {
        setLoadingData(false);
        return;
      }
      if (navigationStore!.userOrganization) {
        navigationStore!.applyThemeColors();
        navigationStore!.getOrganizationDetails(
          navigationStore!.userOrganization.id,
        );
      } else {
        await navigationStore!.tryGetMyOrganization();
      }
      setLoadingData(false);
    };

    const permissions = navigationStore!.userPermissions;
    const subscriptionState =
      navigationStore!.subscriptionStatus === undefined
        ? -1
        : navigationStore!.subscriptionStatus;
    const userOrganization = navigationStore!.userOrganization;
    const features = navigationStore!.subscriptionPermissions;
    const user = userInfoStore!.userInfo;

    const routerKey = JSON.stringify({
      permissions,
      subscriptionState,
      organizationId: userOrganization?.id,
      userType: userOrganization?.userType,
      selectedSubscriptionType: userOrganization?.selectedSubscriptionType,
      features,
      userName: user?.name,
    });

    const appRouter = React.useMemo(
      () =>
        router(
          permissions,
          subscriptionState,
          userOrganization,
          features,
          user,
        ),
      [routerKey],
    );

    return !loadingData ? <RouterProvider router={appRouter} /> : null;
  }),
);

export default AppRouter;
